import { getFlattenedNodes, SummaryType } from "@ls/grouping-service";
import { compareValues, memoize, uniqueByKey } from "@ls/js-utils";
import { createSelector } from "reselect";
import CalcType from "state/Constants/CalcType";
import ScaleType from "state/Constants/ScaleType";
import SelectionState from "state/selections/SelectionState";
import {
  selectSelectedAccountEntityId,
  selectSelectedBenchmarkEntityId,
  selectSelectedAccountEntity,
} from "state/selections/selectors";
import {
  getEntitySummariesObject,
  getEntitySummariesObjectForBench,
} from "state/util";
import scope from "./scope";

export const selectBookAnalyticsData = (summaryType) => (state) =>
  state[scope]["bookAnalyticsData"][summaryType];
export const selectFetchingBookAnalyticsData = (summaryType) => (state) =>
  state[scope]["fetchingBookAnalyticsData"][summaryType];

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return (state) => state[scope]["definitions"][selectionState];
  }

  return createSelector(
    (state) => state[scope]["definitions"][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(
        ({ complete }) => complete,
      );
      return completeDefinitions.filter(
        uniqueByKey(completeDefinitions, "key"),
      );
    },
  );
});

export const selectRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectBookAnalyticsData(SummaryType.ABSOLUTE),
  selectBookAnalyticsData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, absData, relData, accEntityId, bmkEntityId, benchId) => {
    const absoluteNodes = getFlattenedNodes(absData?.root) ?? [];
    const relativeNodes = getFlattenedNodes(relData?.root) ?? [];
    return [...absoluteNodes, ...relativeNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          row[definition.key] = findBookAnalyticValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId,
          );
        });
      return rowMap;
    }, {});
  },
);

const findBookAnalyticValue = (
  node,
  definition,
  accEntityId,
  bmkEntityId,
  benchId,
) => {
  const { bookAnalytic, basis, calcType, scaleType } = definition;
  const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
  const benchValue = calcType === CalcType.Benchmark ? 0 : benchId;
  const isRelative = calcType === CalcType.Relative;
  let entitySummariesObject;
  if (benchValue !== null && benchValue !== undefined) {
    entitySummariesObject = getEntitySummariesObjectForBench(
      node,
      entityId,
      benchValue,
      isRelative,
    );
  } else {
    entitySummariesObject =
      getEntitySummariesObject(node, entityId, isRelative) ?? {};
  }

  const amortBooks = entitySummariesObject?.["amortBooks"] ?? [];

  // Find the amortBook entry that matches the basis
  const amortBook = amortBooks.find((book) => book.costBasis === basis);

  if (!amortBook) {
    return null;
  }

  // Choose rescaled values for WeightedAverage, else raw values
  const sourceValues =
    scaleType === ScaleType.WeightedAverage
      ? amortBook.rescaledValues
      : amortBook.values;

  // Get the specific book analytic value from the chosen source
  const bookAnalyticValue = sourceValues?.[bookAnalytic];

  return bookAnalyticValue ?? null;
};

export const selectFetchingBookBasisValues = (state) =>
  state[scope].fetchingBookBasisValues;
export const selectBookBasisValues = (state) => state[scope].bookBasisValues;
export const selectBookBasisValuesSelectionOptions = createSelector(
  selectBookBasisValues,
  (bookBasisValues) => {
    console.log("Book Basis Values:", bookBasisValues);
    if (bookBasisValues && bookBasisValues.length > 0) {
      return bookBasisValues
        .map((aValue) => ({ label: aValue.toUpperCase(), value: aValue }))
        .sort(compareValues("label"));
    }
    return [];
  },
);
